// @ts-nocheck
import { baseApi } from './base.api';
import {
  API_VERSION,
  PATH_ANALYTICS_API,
  PATH_KNOWLEDGE_BASE_API,
  PATH_PROJECTS_API,
} from '../constants/api-routes';
import { ISO_DATE_FORMAT, URL_REGEX } from 'src/shared/constants';
import moment from 'moment';
import { decodeEntities } from 'src/utils/decodeEntities';
import { ProductKey } from 'src/constants/products';
import { BaseQueryArg, BaseQueryMeta } from '@reduxjs/toolkit/query';

interface Project {
  id: number;
  name: string;
  team_members: { id: number; user: { id: number; name: string } }[];
  project_type: ProductKey;
  integrations: [];
  is_active: boolean;
  cocbi_project_id: number;
  use_cocbi_model: boolean;
  ai_reply_generation: boolean;
  ai_text_lense: boolean;
}

export const projectsService = baseApi.injectEndpoints({
  endpoints: (build) => {
    return {
      getAllProjects: build.query<Project[], void>({
        query: () => {
          const version = localStorage.getItem('API_VERSION') || API_VERSION.V1;

          return {
            url: `${version}/${PATH_PROJECTS_API.ALL}`,
          };
        },
        providesTags: ['Projects'],
        transformResponse(
          baseQueryReturnValue: RawResultType,
          meta: BaseQueryMeta<BaseQuery>,
          arg: QueryArg,
        ): Promise<ResultType> | ResultType {
          const version = localStorage.getItem('API_VERSION') || API_VERSION.V1;

          if (version === API_VERSION.V2) {
            return baseQueryReturnValue.items;
          }

          return baseQueryReturnValue;
        },
      }),
      getProjectsStatisticsSummary: build.query({
        query: ({ start_date, end_date, cocustomer_project_ids, coagent_project_ids }) => ({
          url: `${API_VERSION.V1}/${PATH_ANALYTICS_API.SUMMARY}?${cocustomer_project_ids}&${coagent_project_ids}`,
          params: {
            start_date,
            end_date,
          },
        }),
      }),
      getProjectById: build.query<Project, { id: number | string }>({
        query: ({ id }) => {
          const version = localStorage.getItem('API_VERSION') || API_VERSION.V1;

          return {
            url: `${version}/${PATH_PROJECTS_API.ROOT}/${id}`,
          };
        },
        providesTags: ['ProjectById'],
      }),
      getAllIntegrations: build.query<Project, { id: number | string }>({
        query: ({ id }) => {
          const version = localStorage.getItem('API_VERSION') || API_VERSION.V1;

          return {
            url: `${version}/${PATH_PROJECTS_API.ROOT}/${id}/${PATH_PROJECTS_API.INTEGRATIONS}`,
          };
        },
        async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
          try {
            const { data: integrations } = await queryFulfilled;

            dispatch(
              projectsService.util.updateQueryData('getProjectById', { id: `${id}` }, (draft) => {
                draft.integrations = integrations || [];
              }),
            );
          } catch (err) {
            console.error('Error get project team members:', err);
          }
        },
        providesTags: ['getAllIntegrations'],
      }),
      getProjectTeamMembers: build.query({
        query: ({ id }) => {
          const version = localStorage.getItem('API_VERSION') || API_VERSION.V1;

          return {
            url: `${version}/${PATH_PROJECTS_API.ROOT}/${id}/${PATH_PROJECTS_API.TEAM_MEMBERS}`,
          };
        },
        async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
          try {
            const { data: teamMembers } = await queryFulfilled;

            dispatch(
              projectsService.util.updateQueryData('getProjectById', { id: `${id}` }, (draft) => {
                draft.team_members = teamMembers;
              }),
            );
          } catch (err) {
            console.error('Error get project team members:', err);
          }
        },
        providesTags: ['ProjectTeamMembers'],
      }),
      addProjectTeamMembers: build.mutation({
        query: ({ id, users, type }) => {
          const version = localStorage.getItem('API_VERSION') || API_VERSION.V1;

          return {
            url: `${version}/${PATH_PROJECTS_API.ROOT}/${id}/${PATH_PROJECTS_API.TEAM_MEMBERS}`,
            params: {
              ...(type !== '' && { project_type: type }),
            },
            method: 'POST',
            body: {
              users: users,
            },
          };
        },
        invalidatesTags: ['ProjectTeamMembers', 'ProjectById'],
      }),
      deleteProjectTeamMembers: build.mutation({
        query: ({ id, teamMemberId, type }) => {
          const version = localStorage.getItem('API_VERSION') || API_VERSION.V1;

          return {
            url: `${version}/${PATH_PROJECTS_API.ROOT}/${id}/${PATH_PROJECTS_API.TEAM_MEMBERS}/${teamMemberId}`,
            params: {
              ...(type !== '' && { project_type: type }),
            },
            method: 'DELETE',
          };
        },
        async onQueryStarted({ id, teamMemberId }, { dispatch, queryFulfilled }) {
          try {
            await queryFulfilled;
            dispatch(
              projectsService.util.updateQueryData('getProjectById', { id: `${id}` }, (draft) => {
                draft.team_members = draft.team_members.filter(
                  (member) => Number(member.id) !== Number(teamMemberId),
                );
              }),
            );
          } catch (err) {
            console.error('Error updating project team members:', err);
          }
        },
        invalidatesTags: (result, error, { id }) => [{ type: 'ProjectTeamMembers', id }],
      }),
      newLimitProjectTeamMembers: build.mutation({
        query: ({ id, teamMemberId, limit }) => ({
          url: `${API_VERSION.V1}/${PATH_PROJECTS_API.ROOT}/${id}/${PATH_PROJECTS_API.TEAM_MEMBERS}/${teamMemberId}/${PATH_PROJECTS_API.NEW_LIMIT}`,
          method: 'PUT',
          body: {
            limit,
          },
        }),
        async onQueryStarted({ id, teamMemberId }, { dispatch, queryFulfilled }) {
          try {
            const { data: updatedTeamMember } = await queryFulfilled;
            await queryFulfilled;
            dispatch(
              projectsService.util.updateQueryData('getProjectById', { id: `${id}` }, (draft) => {
                const currentMember = draft.team_members.find(
                  (member) => member.id === updatedTeamMember.id,
                );

                if (currentMember) {
                  currentMember.requests_left = updatedTeamMember.requests_left;
                }
              }),
            );
          } catch (err) {
            console.error('Error updating project team members:', err);
          }
        },
        invalidatesTags: (result, error, { id }) => [{ type: 'ProjectTeamMembers', id }],
      }),
      updateProject: build.mutation({
        query: ({
          projectId,
          name,
          ai_reply_generation,
          ai_text_lense,
          cocbi_project_id = 0,
          use_cocbi_model = false,
          ...rest
        }) => {
          const version = localStorage.getItem('API_VERSION') || API_VERSION.V1;

          return {
            url: `${version}/${PATH_PROJECTS_API.ROOT}/${projectId}`,
            method: version === API_VERSION.V1 ? 'PUT' : 'PATCH',
            body: {
              name,
              cocbi_project_id,
              use_cocbi_model,
              ai_reply_generation,
              ai_text_lense,
              ...rest,
            },
          };
        },
        async onQueryStarted({ projectId }, { dispatch, queryFulfilled }) {
          try {
            const { data: updatedProject } = await queryFulfilled;
            dispatch(
              projectsService.util.updateQueryData('getAllProjects', undefined, (draft) => {
                const currentProject = draft.find((project) => project.id === updatedProject.id);
                Object.assign(currentProject, updatedProject);
              }),
            );
            const { integrations, ...rest } = updatedProject;
            dispatch(
              projectsService.util.updateQueryData(
                'getProjectById',
                { id: `${projectId}` },
                (draft) => {
                  Object.assign(draft, rest);
                },
              ),
            );
          } catch {}
        },
        invalidatesTags: ['Projects', 'ProjectById'],
      }),
      deleteProject: build.mutation({
        query: ({ projectId, ...rest }) => {
          const version = localStorage.getItem('API_VERSION') || API_VERSION.V1;

          return {
            url: `${version}/${PATH_PROJECTS_API.ROOT}/${projectId}`,
            method: 'DELETE',
            body: rest,
          };
        },
        async onQueryStarted({ projectId }, { dispatch, queryFulfilled }) {
          try {
            await queryFulfilled;
            dispatch(
              projectsService.util.updateQueryData('getAllProjects', undefined, (draft) => {
                return draft?.filter((project) => +project.id !== +projectId);
              }),
            );
          } catch {}
        },
        invalidatesTags: ['Projects'],
      }),
      createProject: build.mutation({
        query: (body) => {
          const version = localStorage.getItem('API_VERSION') || API_VERSION.V1;

          return {
            url: `${version}/${PATH_PROJECTS_API.ROOT}`,
            method: 'POST',
            body,
          };
        },
        async onQueryStarted(args, { dispatch, queryFulfilled }) {
          try {
            const { data: createdProject } = await queryFulfilled;
            dispatch(
              projectsService.util.updateQueryData('getAllProjects', undefined, (draft) => {
                draft?.push(createdProject);
              }),
            );
          } catch {}
        },
      }),
      activateProject: build.mutation({
        query: ({ projectId }) => {
          const version = localStorage.getItem('API_VERSION') || API_VERSION.V1;

          return {
            url: `${version}/${PATH_PROJECTS_API.ROOT}/${projectId}/activate`,
            method: 'PUT',
          };
        },
        async onQueryStarted({ projectId }, { dispatch, queryFulfilled }) {
          try {
            const { data: updatedProject } = await queryFulfilled;
            dispatch(
              projectsService.util.updateQueryData('getAllProjects', undefined, (draft) => {
                const currentProject = draft.find((project) => project.id === updatedProject.id);
                currentProject.is_active = updatedProject.is_active;
              }),
            );
          } catch {}
        },
        invalidatesTags: (result, error, { id }) => [{ type: 'ProjectById', id }],
      }),
      deactivateProject: build.mutation({
        query: ({ projectId }) => {
          const version = localStorage.getItem('API_VERSION') || API_VERSION.V1;

          return {
            url: `${version}/${PATH_PROJECTS_API.ROOT}/${projectId}/deactivate`,
            method: 'PUT',
          };
        },
        async onQueryStarted({ projectId }, { dispatch, queryFulfilled }) {
          try {
            const { data: updatedProject } = await queryFulfilled;
            dispatch(
              projectsService.util.updateQueryData('getAllProjects', undefined, (draft) => {
                const currentProject = draft.find((project) => project.id === updatedProject.id);
                currentProject.is_active = updatedProject.is_active;
              }),
            );
          } catch {}
        },
        invalidatesTags: (result, error, { id }) => [{ type: 'ProjectById', id }],
      }),
      addIntegration: build.mutation({
        query: ({ id, body }) => {
          const version = localStorage.getItem('API_VERSION') || API_VERSION.V1;

          return {
            url: `${version}/${PATH_PROJECTS_API.ROOT}/${id}/${PATH_PROJECTS_API.INTEGRATIONS}`,
            method: 'POST',
            body: body,
          };
        },
      }),
      getProjectIntegrationsList: build.query({
        query: ({ project_id, skip_dispatch = false }) => {
          const version = localStorage.getItem('API_VERSION') || API_VERSION.V1;

          return {
            url: `${version}/${PATH_PROJECTS_API.ROOT}/${project_id}/${PATH_PROJECTS_API.INTEGRATIONS}`,
          };
        },
        async onQueryStarted({ project_id, skip_dispatch }, { dispatch, queryFulfilled }) {
          if (skip_dispatch) {
            return;
          }

          try {
            const { data: integrationsList } = await queryFulfilled;
            dispatch(
              projectsService.util.updateQueryData(
                'getProjectById',
                { id: `${project_id}` },
                (draft) => {
                  draft.integrations = integrationsList;
                },
              ),
            );
          } catch (err) {
            console.error('Error get project integrations:', err);
          }
        },
        providesTags: ['getProjectIntegrationsList'],
      }),
      getZendeskConsentLink: build.query({
        query: ({ project_id, integration_id, type = 'zendesk-email' }) => {
          const version = localStorage.getItem('API_VERSION') || API_VERSION.V1;

          return {
            url: `${version}/${PATH_PROJECTS_API.ROOT}/${project_id}/${PATH_PROJECTS_API.INTEGRATIONS}/${integration_id}/${type}/${PATH_PROJECTS_API.INTEGRATIONS_ZENDESK_OAUTH_AUTHORIZE_URL}`,
          };
        },
        keepUnusedDataFor: 0,
        providesTags: ['getZendeskConsentLink'],
      }),
      projectDemoMode: build.mutation({
        query: ({ id, project_type, enable }) => ({
          url: `${API_VERSION.V1}/${PATH_PROJECTS_API.DEMO_MODE}/${id}`,
          method: 'POST',
          params: {
            project_id: id,
            project_type,
            enable,
          },
        }),
        async onQueryStarted({ id, enable }, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled;

            dispatch(
              projectsService.util.updateQueryData('getProjectById', { id: `${id}` }, (draft) => {
                draft.is_demo_mode = enable;
              }),
            );
          } catch (err) {
            console.error('Error add project integration:', err);
          }
        },
        invalidatesTags: (result, error, { id }) => [{ type: 'ProjectById', id }],
      }),
      updateIntegration: build.mutation({
        query: ({ projectId, integrationId, body }) => {
          const version = localStorage.getItem('API_VERSION') || API_VERSION.V1;

          return {
            url: `${version}/${PATH_PROJECTS_API.ROOT}/${projectId}/${PATH_PROJECTS_API.INTEGRATIONS}/${integrationId}`,
            method: 'PUT',
            body: body,
          };
        },
        async onQueryStarted({ projectId }, { dispatch, queryFulfilled }) {
          try {
            const { data: updatedIntegration } = await queryFulfilled;
            dispatch(
              projectsService.util.updateQueryData(
                'getProjectById',
                { id: `${projectId}` },
                (draft) => {
                  const index = draft.integrations.findIndex(
                    (integration) =>
                      integration.integration_id === updatedIntegration.integration_id,
                  );

                  if (index !== -1) {
                    draft.integrations[index] = updatedIntegration;
                  }
                },
              ),
            );
          } catch (err) {
            console.error('Error updating project integration:', err);
          }
        },
      }),
      zendeskEmailIntegrationAutoSetup: build.mutation({
        query: ({ projectId, integrationId, body }) => {
          const version = localStorage.getItem('API_VERSION') || API_VERSION.V1;

          return {
            url: `${version}/${PATH_PROJECTS_API.ROOT}/${projectId}/${PATH_PROJECTS_API.INTEGRATIONS}/${integrationId}/${PATH_PROJECTS_API.INTEGRATIONS_ZENDESK_EMAIL_AUTO_SETUP}`,
            method: 'POST',
            body: {
              ...(body.escalated_assignee_id && {
                escalated_assignee_id: body.escalated_assignee_id,
              }),
              ...(body.escalated_custom_status_id && {
                escalated_custom_status_id: body.escalated_custom_status_id,
              }),
              ...(body.escalated_group_id && {
                escalated_group_id: body.escalated_group_id,
              }),
            },
          };
        },
      }),
      getZendeskEmailStatusFields: build.query({
        query: ({ projectId, integrationId, type }) => {
          const version = localStorage.getItem('API_VERSION') || API_VERSION.V1;

          return {
            url: `${version}/${PATH_PROJECTS_API.ROOT}/${projectId}/${PATH_PROJECTS_API.INTEGRATIONS}/${integrationId}/${PATH_PROJECTS_API.INTEGRATIONS_ZENDESK_EMAIL_STATUS_FIELDS}`,
            params: {
              ...(type !== '' && { project_type: type }),
            },
          };
        },
      }),
      getZendeskEmailAssigneeFields: build.query({
        query: ({ projectId, integrationId, query }) => {
          const version = localStorage.getItem('API_VERSION') || API_VERSION.V1;

          return {
            url: `${version}/${PATH_PROJECTS_API.ROOT}/${projectId}/${PATH_PROJECTS_API.INTEGRATIONS}/${integrationId}/${PATH_PROJECTS_API.INTEGRATIONS_ZENDESK_EMAIL_ASSIGNEE_FIELDS}${query ? `?${query}` : ''}`,
          };
        },
      }),
      getZendeskEmailGroupsFields: build.query({
        query: ({ projectId, integrationId, page }) => {
          const version = localStorage.getItem('API_VERSION') || API_VERSION.V1;

          return {
            url: `${version}/${PATH_PROJECTS_API.ROOT}/${projectId}/${PATH_PROJECTS_API.INTEGRATIONS}/${integrationId}/${PATH_PROJECTS_API.INTEGRATIONS_ZENDESK_EMAIL_GROUPS_FIELDS}`,
            params: {
              page: page,
            },
          };
        },
      }),
      getFreshdeskEmailAgents: build.query({
        query: ({ projectId, integrationId, query }) => {
          const version = localStorage.getItem('API_VERSION') || API_VERSION.V1;

          return {
            url: `${version}/${PATH_PROJECTS_API.ROOT}/${projectId}/${PATH_PROJECTS_API.INTEGRATIONS}/${integrationId}/${PATH_PROJECTS_API.INTEGRATIONS_FRESHDESK_EMAIL_AGENTS}${query ? `?${query}` : ''}`,
          };
        },
      }),
      getFreshdeskEmailGroups: build.query({
        query: ({ projectId, integrationId, query }) => {
          const version = localStorage.getItem('API_VERSION') || API_VERSION.V1;

          return {
            url: `${version}/${PATH_PROJECTS_API.ROOT}/${projectId}/${PATH_PROJECTS_API.INTEGRATIONS}/${integrationId}/${PATH_PROJECTS_API.INTEGRATIONS_FRESHDESK_EMAIL_GROUPS}${query ? `?${query}` : ''}`,
          };
        },
      }),
      freshdeskEmailIntegrationAutoSetup: build.mutation({
        query: ({ projectId, integrationId, body }) => {
          const version = localStorage.getItem('API_VERSION') || API_VERSION.V1;

          return {
            url: `${version}/${PATH_PROJECTS_API.ROOT}/${projectId}/${PATH_PROJECTS_API.INTEGRATIONS}/${integrationId}/${PATH_PROJECTS_API.INTEGRATIONS_FRESHDESK_EMAIL_AUTO_SETUP}`,
            method: 'POST',
            body: {
              responder_id: body.responder_id,
              responder_email: body.responder_email,
              ...(body.escalate_assign_group_id && {
                escalate_assign_group_id: body.escalate_assign_group_id,
              }),
            },
          };
        },
      }),
      zendeskChatIntegrationAutoSetup: build.mutation({
        query: ({ projectId, integrationId, body }) => {
          const version = localStorage.getItem('API_VERSION') || API_VERSION.V1;

          return {
            url: `${version}/${PATH_PROJECTS_API.ROOT}/${projectId}/${PATH_PROJECTS_API.INTEGRATIONS}/${integrationId}/${PATH_PROJECTS_API.INTEGRATIONS_ZENDESK_CHAT_AUTO_SETUP}`,
            method: 'POST',
            body: {
              widget_integration_id: body.widget_integration_id,
              responder_mode: body.responder_mode || 'default_responder',
            },
          };
        },
        invalidatesTags: (result, error, { id }) => [{ type: 'ProjectById', id }],
      }),
      getZendeskChatWidgets: build.query({
        query: ({ projectId, integrationId }) => {
          const version = localStorage.getItem('API_VERSION') || API_VERSION.V1;

          return {
            url: `${version}/${PATH_PROJECTS_API.ROOT}/${projectId}/${PATH_PROJECTS_API.INTEGRATIONS}/${integrationId}/${PATH_PROJECTS_API.ZENDESK_CHAT_WIDGETS}`,
            params: {
              filter_types: 'web',
            },
          };
        },
      }),
      getZohoSalesIQSetup: build.mutation({
        query: ({ projectId, integrationId }) => {
          const version = localStorage.getItem('API_VERSION') || API_VERSION.V1;

          return {
            url: `${version}/${PATH_PROJECTS_API.ROOT}/${projectId}/${PATH_PROJECTS_API.INTEGRATIONS}/${integrationId}/${PATH_PROJECTS_API.INTEGRATIONS_ZOHO_SALES_IQ_SETUP}`,
            method: 'POST',
          };
        },
      }),
      getZohoEmailSetup: build.mutation({
        query: ({ projectId, integrationId }) => {
          const version = localStorage.getItem('API_VERSION') || API_VERSION.V1;

          return {
            url: `${version}/${PATH_PROJECTS_API.ROOT}/${projectId}/${PATH_PROJECTS_API.INTEGRATIONS}/${integrationId}/${PATH_PROJECTS_API.INTEGRATIONS_ZOHO_DESK_SETUP}`,
            method: 'POST',
          };
        },
      }),
      deleteIntegration: build.mutation({
        query: ({ projectId, integrationId }) => {
          const version = localStorage.getItem('API_VERSION') || API_VERSION.V1;

          return {
            url: `${version}/${PATH_PROJECTS_API.ROOT}/${projectId}/${PATH_PROJECTS_API.INTEGRATIONS}/${integrationId}`,
            method: 'DELETE',
          };
        },
        async onQueryStarted({ projectId, integrationId }, { dispatch, queryFulfilled }) {
          try {
            await queryFulfilled;
            dispatch(
              projectsService.util.updateQueryData(
                'getProjectById',
                { id: `${projectId}` },
                (draft) => {
                  const index = draft.integrations.findIndex(
                    (integration) => integration.integration_id === integrationId,
                  );

                  draft.integrations.splice(index, 1);
                },
              ),
            );
          } catch (err) {
            console.error('Error deleting project integration:', err);
          }
        },
        invalidatesTags: (result, error, { id }) => [{ type: 'ProjectById', id }],
      }),
      getProjectFiles: build.query({
        query: ({ projectId, sync }) => ({
          url: `${API_VERSION.V1}/${PATH_PROJECTS_API.ROOT}/${projectId}/${PATH_PROJECTS_API.FILES}`,
          params: { sync: sync },
        }),
        async onQueryStarted({ projectId }, { dispatch, queryFulfilled }) {
          try {
            const { data: files } = await queryFulfilled;

            dispatch(
              projectsService.util.updateQueryData(
                'getProjectById',
                { id: `${projectId}` },
                (draft) => {
                  draft.files = files;
                },
              ),
            );
          } catch (err) {
            console.error('Error get project files:', err);
          }
        },
        providesTags: ['ProjectFiles'],
      }),
      addProjectFile: build.mutation({
        query: ({ projectId, body }) => ({
          url: `${API_VERSION.V1}/${PATH_PROJECTS_API.ROOT}/${projectId}/${PATH_PROJECTS_API.FILES}`,
          method: 'POST',
          body: body,
          formData: true,
        }),
        async onQueryStarted({ projectId, body }, { dispatch, queryFulfilled }) {
          try {
            const { data: file } = await queryFulfilled;

            dispatch(
              projectsService.util.updateQueryData(
                'getProjectById',
                { id: `${projectId}` },
                (draft) => {
                  draft.files = draft.files || [];
                  draft.files.push(file);
                },
              ),
            );
          } catch (err) {
            console.error('Error add project files:', err);
          }
        },
      }),
      addProjectDocument: build.mutation({
        query: ({ projectId, body }) => ({
          url: `${API_VERSION.V1}/${PATH_PROJECTS_API.ROOT}/${projectId}/${PATH_PROJECTS_API.ADD_DOCUMENT}`,
          method: 'POST',
          body: body,
        }),
        async onQueryStarted({ projectId, body }, { dispatch, queryFulfilled }) {
          try {
            const { data: file } = await queryFulfilled;

            dispatch(
              projectsService.util.updateQueryData(
                'getProjectById',
                { id: `${projectId}` },
                (draft) => {
                  draft.files = draft.files || [];
                  draft.files.push(file);
                },
              ),
            );
          } catch (err) {
            console.error('Error add project document:', err);
          }
        },
      }),
      updateProjectFiles: build.mutation({
        query: ({ projectId, body }) => ({
          url: `${API_VERSION.V1}/${PATH_PROJECTS_API.ROOT}/${projectId}/${PATH_PROJECTS_API.FILES}`,
          method: 'PATCH',
          body: body,
        }),
        invalidatesTags: ['ProjectFiles'],
      }),
      deleteProjectFile: build.mutation({
        query: ({ projectId, fileId }) => ({
          url: `${API_VERSION.V1}/${PATH_PROJECTS_API.ROOT}/${projectId}/${PATH_PROJECTS_API.FILES}/${fileId}`,
          method: 'DELETE',
        }),
        async onQueryStarted({ projectId, fileId }, { dispatch, queryFulfilled }) {
          try {
            dispatch(
              projectsService.util.updateQueryData(
                'getProjectById',
                { id: `${projectId}` },
                (draft) => {
                  draft.files = draft.files.filter((file) => file.id !== fileId);
                },
              ),
            );
          } catch (err) {
            console.error('Error deleting project file:', err);
          }
        },
        invalidatesTags: ['ProjectFiles'],
      }),
      deleteProjectFiles: build.mutation({
        query: ({ projectId, body }) => ({
          url: `${API_VERSION.V1}/${PATH_PROJECTS_API.ROOT}/${projectId}/${PATH_PROJECTS_API.FILES}`,
          method: 'DELETE',
          body: body,
        }),
        async onQueryStarted({ projectId, body }, { dispatch, queryFulfilled }) {
          try {
            dispatch(
              projectsService.util.updateQueryData(
                'getProjectById',
                { id: `${projectId}` },
                (draft) => {
                  draft.files = draft.files.filter((file) => !body.includes(file.id));
                },
              ),
            );
          } catch (err) {
            console.error('Error deleting project file:', err);
          }
        },
      }),
      assignProjectKnowledgeBase: build.mutation({
        query: ({ projectId, project_type, body }) => {
          const version = localStorage.getItem('API_VERSION') || API_VERSION.V1;
          const path =
            version === API_VERSION.V1
              ? PATH_KNOWLEDGE_BASE_API.ASSIGN
              : PATH_KNOWLEDGE_BASE_API.ASSIGN_V2;

          return {
            url: `${version}/${PATH_PROJECTS_API.ROOT}/${projectId}/${path}`,
            method: 'POST',
            params: {
              project_type,
            },
            body: body,
          };
        },
        invalidatesTags: ['KnowledgeBase'],
      }),
      unassignProjectKnowledgeBase: build.mutation({
        query: ({ projectId, project_type, body }) => {
          const version = localStorage.getItem('API_VERSION') || API_VERSION.V1;
          const path =
            version === API_VERSION.V1
              ? PATH_KNOWLEDGE_BASE_API.UNASSIGN
              : PATH_KNOWLEDGE_BASE_API.UNASSIGN_V2;

          return {
            url: `${version}/${PATH_PROJECTS_API.ROOT}/${projectId}/${path}`,
            method: 'POST',
            params: {
              project_type,
            },
            body: body,
          };
        },
        invalidatesTags: ['KnowledgeBase'],
      }),
      getProjectChats: build.query({
        query: ({
          project_id = '',
          service = '',
          created_at_start = '',
          created_at_end = '',
          updated_at_start = '',
          updated_at_end = '',
          page = '',
          page_size = '',
          chat_status = '',
          account = '',
          order_by = '',
          types = '',
          sync,
        }) => {
          const version = localStorage.getItem('API_VERSION') || API_VERSION.V1;

          return {
            url: `${version}/${PATH_PROJECTS_API.ROOT}/${project_id}/${PATH_PROJECTS_API.CHATS}?${chat_status
              .map((status) => {
                return `chat_status=${status}`;
              })
              .join('&')}&${types
              .map((type) => {
                return `types=${type}`;
              })
              .join('&')}`,
            params: {
              sync: sync,
              ...(project_id !== '' && { project_id }),
              ...(service !== '' && { service }),
              ...(created_at_start !== '' && {
                created_at_start: moment(created_at_start).utc().format(ISO_DATE_FORMAT),
              }),
              ...(created_at_end !== '' && {
                created_at_end: moment(created_at_end).utc().format(ISO_DATE_FORMAT),
              }),
              ...(updated_at_start !== '' && {
                updated_at_start: moment(updated_at_start).utc().format(ISO_DATE_FORMAT),
              }),
              ...(updated_at_end !== '' && {
                updated_at_end: moment(updated_at_end).utc().format(ISO_DATE_FORMAT),
              }),
              ...(page !== '' && { page }),
              ...(page_size !== '' && { page_size }),
              ...(account !== '' && { account }),
              ...(order_by !== '' && { order_by }),
            },
          };
        },
        transformResponse(response) {
          const results = response.results.map((chat) => {
            const userMessage = chat.messages.find(
              (message) => message.owner === 'user' && Boolean(message.body),
            );

            // Convert dates to the local time
            chat.updated_at = moment.utc(chat.updated_at).local().format(ISO_DATE_FORMAT);
            chat.created_at = moment.utc(chat.created_at).local().format(ISO_DATE_FORMAT);
            // Create subject based on first message
            chat.subject = decodeEntities(
              userMessage ? userMessage.body : chat.messages[0] ? chat.messages[0].body : '...',
            );

            if (chat.status === 'open') {
              chat.friendlyStatus = 'In Progress';
            }
            if (['closed', 'auto_closed'].includes(chat.status)) {
              chat.friendlyStatus = 'Resolved';
            }
            if (chat.status === 'escalated') {
              chat.friendlyStatus = 'Escalated';
            }

            chat.messages.forEach((message) => {
              const matches = message.body.match(URL_REGEX);

              // Make links in Conversations History Page clickable
              if (matches) {
                for (const match of matches) {
                  const prettyUrl = match.replace(/\'/g, '');
                  // convert link to Markdown format like: [link](https://cosupport.ai)
                  message.body = message.body.replace(match, `[${prettyUrl}](${prettyUrl})`);
                }
              }

              // Convert date to the local time
              message.created_at = moment.utc(message.created_at).local().format(ISO_DATE_FORMAT);
            });

            return chat;
          });

          return {
            ...response,
            results,
          };
        },
        async onQueryStarted({ project_id }, { dispatch, queryFulfilled }) {
          try {
            const { data: chats } = await queryFulfilled;

            dispatch(
              projectsService.util.updateQueryData(
                'getProjectById',
                { id: `${project_id}` },
                (draft) => {
                  draft.chats = chats;
                },
              ),
            );
          } catch (err) {
            console.error('Error get project files:', err);
          }
        },
        providesTags: ['ProjectChats'],
      }),
    };
  },
});

export const {
  useUpdateProjectMutation,
  useGetProjectByIdQuery,
  useGetAllProjectsQuery,
  useDeleteProjectMutation,
  useCreateProjectMutation,
  useGetProjectTeamMembersQuery,
  useAddProjectTeamMembersMutation,
  useDeleteProjectTeamMembersMutation,
  useNewLimitProjectTeamMembersMutation,
  useActivateProjectMutation,
  useDeactivateProjectMutation,
  useAddIntegrationMutation,
  useGetZendeskConsentLinkQuery,
  useLazyGetZendeskConsentLinkQuery,
  useGetProjectIntegrationsListQuery,
  useLazyGetProjectIntegrationsListQuery,
  useUpdateIntegrationMutation,
  useDeleteIntegrationMutation,
  useGetProjectFilesQuery,
  useAddProjectFileMutation,
  useAddProjectDocumentMutation,
  useUpdateProjectFilesMutation,
  useDeleteProjectFilesMutation,
  useDeleteProjectFileMutation,
  useProjectDemoModeMutation,
  useLazyGetProjectChatsQuery,
  useZendeskEmailIntegrationAutoSetupMutation,
  useGetFreshdeskEmailAgentsQuery,
  useGetFreshdeskEmailGroupsQuery,
  useFreshdeskEmailIntegrationAutoSetupMutation,
  useGetZendeskEmailStatusFieldsQuery,
  useGetZendeskEmailAssigneeFieldsQuery,
  useGetZendeskEmailGroupsFieldsQuery,
  useAssignProjectKnowledgeBaseMutation,
  useUnassignProjectKnowledgeBaseMutation,
  useZendeskChatIntegrationAutoSetupMutation,
  useGetZendeskChatWidgetsQuery,
  useGetZohoSalesIQSetupMutation,
  useGetZohoEmailSetupMutation,
  useGetProjectsStatisticsSummaryQuery,
} = projectsService;
